import React, {
  useState, useMemo, useCallback,
  useEffect,
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './style.scss';
import dayjs from 'dayjs';
import { toISTDate } from '@Apis/constants';
import PRZInput from '../UI/PRZInput';
import PRZSelect from '../UI/PRZSelect';
import PRZDatePicker from '../UI/PRZDatePicker';
import PRZButton from '../UI/PRZButton';
import productV2Actions from '../../../actions/inventory/productV2Actions';

const LedgerEntryForm = ({
  user, isUpdateLedgerEntry, selectLedgerEntry, selectedSeller, selectedCustomer, createLedgerEntry, createLedgerEntryLoading, updateLedgerEntry, updateLedgerEntryLoading, callback,
}) => {
  const initialFormData = useMemo(() => ({
    refNumber: '',
    transactionAmount: 0,
    transactionType: 'DEBIT',
    transactionDate: '', // today date
  }), []);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (isUpdateLedgerEntry && selectLedgerEntry) {
      setFormData({
        refNumber: selectLedgerEntry?.reference_number || '',
        transactionAmount: selectLedgerEntry?.transaction_amount || 0,
        transactionType: selectLedgerEntry?.transaction_type?.toUpperCase(),
        transactionDate: dayjs(selectLedgerEntry?.created_at),
      });
    }
  }, [selectLedgerEntry]);

  const handleFormDataChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const isDataValid = useCallback(() => {
    if (formData.transactionAmount==null || formData.transactionAmount==undefined || !formData.transactionType || !formData.transactionDate) {
      return false;
    }
    return true;
  });

  const handleFormSubmit = () => {
    setFormSubmitted(true);
    if (isDataValid()) {
      const payload = {
        transaction_amount: formData.transactionAmount,
        transaction_type: formData.transactionType,
        tenant_id: user?.tenant_info?.tenant_id,
        entity_type: 'MANUAL_ENTRY',
        entity_status: 'CONFIRMED',
        reference_number: formData.refNumber,
        created_by: user?.user_id,
        created_at: toISTDate(formData.transactionDate),
      };

      if (selectedSeller) {
        payload.seller_id = selectedSeller?.seller_id;
      }

      if (selectedCustomer) {
        payload.customer_id = selectedCustomer?.customer_id;
      }

      if (isUpdateLedgerEntry && selectLedgerEntry) {
        payload.ledger_id = selectLedgerEntry?.ledger_id;

        updateLedgerEntry(payload, () => {
          setFormSubmitted(false);
          setFormData(initialFormData);
          callback();
        });
      } else {
        createLedgerEntry(payload, () => {
          setFormSubmitted(false);
          setFormData(initialFormData);
          callback();
        });
      }
    }
  };

  return (
    <div className="ledger_entry_form__wrapper">
      <div className="ant-row">
        <div className="ant-col-md-24">
          <div className="orgInputContainer">
            <label className="orgFormLabel">
              Ref Number#
            </label>
            <PRZInput
              value={formData.refNumber}
              type="text"
              className="custom-input-config"
              onChange={(e) => {
                const { value } = e.target;
                // if (/^[a-zA-Z0-9]*$/.test(value)) {
                //   handleFormDataChange('refNumber', value);
                // }
                handleFormDataChange('refNumber', value);
              }}
            />
          </div>
        </div>
        <div className="ant-col-md-24">
          <div className="orgInputContainer">
            <label className="orgFormLabel">
              {`Transaction Amount (${selectedSeller?.currency_info?.currency_symbol || selectedCustomer?.currency_info?.currency_symbol})`}
              <span style={{ color: 'red', marginLeft: '5px' }}>*</span>
            </label>
            <PRZInput
              value={formData.transactionAmount}
              type="number"
              className="custom-input-config"
              onChange={(e) => {
                const value = Math.abs(e.target.value);
                setFormData({ ...formData, transactionAmount: value });
              }}
            />
            {formSubmitted && formData.transactionAmount < 0 && <div className="formError">*Please enter valid amount</div>}
          </div>
        </div>
        <div className="ant-col-md-24">
          <div className="orgInputContainer">
            <label className="orgFormLabel">
              Transaction Type
              <span style={{ color: 'red', marginLeft: '5px' }}>*</span>
            </label>
            <PRZSelect
              value={formData.transactionType}
              options={[{ value: 'DEBIT', label: 'Debit' }, { value: 'CREDIT', label: 'Credit' }]}
              onChange={(value) => {
                handleFormDataChange('transactionType', value);
              }}
              className="custom-input-config"
            />
            {formSubmitted && !formData.transactionType && <div className="formError">*Please select valid transaction type</div>}
          </div>
        </div>
        <div className="ant-col-md-24">
          <div className="orgInputContainer">
            <label className="orgFormLabel">
              Transaction Date
              <span style={{ color: 'red', marginLeft: '5px' }}>*</span>
            </label>
            <PRZDatePicker
              className="custom_date_picker custom-input-config"
              value={formData.transactionDate}
              startOfDay
              allowClear={false}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              // disabledDate={(current) => {
              //   const joiningDate = selectedSeller?.seller_info?.created_date || selectedCustomer?.customer_info?.created_date;
              //   return ((current && current > dayjs().endOf('day')) || (joiningDate && current < dayjs(joiningDate).startOf('day')));
              // }}
              onChange={(value) => {
                handleFormDataChange('transactionDate', value);
              }}
            />
            {formSubmitted && !formData.transactionDate && <div className="formError">*Please select valid date</div>}
          </div>
        </div>
      </div>
      <div className="custom-drawer__footer">
        <div className="ant-col-md-6">
          <PRZButton
            className="ledger_entry_submit_btn"
            onClick={handleFormSubmit}
            loading={createLedgerEntryLoading || updateLedgerEntryLoading}
            disabled={!(selectedSeller || selectedCustomer)}
          >
            {isUpdateLedgerEntry ? '+ Update Entry' : '+ Add Entry'}
          </PRZButton>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ UserReducers, ProductV2Reducers }) => ({
  user: UserReducers.user,
  createLedgerEntryLoading: ProductV2Reducers.createLedgerEntryLoading,
  updateLedgerEntryLoading: ProductV2Reducers.updateLedgerEntryLoading,
});

const mapDispatchToProps = (dispatch) => ({
  createLedgerEntry: (payload, callback) => dispatch(productV2Actions.createLedgerEntry(payload, callback)),
  updateLedgerEntry: (payload, callback) => dispatch(productV2Actions.updateLedgerEntry(payload, callback)),
});

LedgerEntryForm.propTypes = {
  isUpdateLedgerEntry: PropTypes.bool,
  selectLedgerEntry: PropTypes.object,
  selectedSeller: PropTypes.object,
  selectedCustomer: PropTypes.object,
  createLedgerEntry: PropTypes.func,
  createLedgerEntryLoading: PropTypes.bool,
  updateLedgerEntry: PropTypes.func,
  updateLedgerEntryLoading: PropTypes.bool,
  callback: PropTypes.func,
  user: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(LedgerEntryForm));
